import React, { useState, useEffect, useCallback } from 'react';
import { commercialOfferService, clientService, productService } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Search, X, FileText, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'da_valutare', label: 'Da valutare', cls: 'bg-slate-100 text-slate-700' },
  { value: 'in_preparazione', label: 'In preparazione', cls: 'bg-amber-100 text-amber-800' },
  { value: 'inviata', label: 'Inviata', cls: 'bg-blue-100 text-blue-800' },
  { value: 'accettata', label: 'Accettata', cls: 'bg-emerald-100 text-emerald-800' },
  { value: 'rifiutata', label: 'Rifiutata', cls: 'bg-red-100 text-red-800' },
];

const statusBadge = (s) => {
  const opt = STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${opt.cls}`}>{opt.label}</span>;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function OffersListPage() {
  const nav = useNavigate();
  const [offers, setOffers] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Filters
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');

  function emptyForm() {
    return { client_id: '', request_date: new Date().toISOString().slice(0, 10), sent_date: '', subject: '', product_id: '', status: 'da_valutare', notes: '' };
  }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterClient) params.client_id = filterClient;
      if (filterStatus) params.status = filterStatus;
      if (filterYear) params.year = filterYear;
      if (search) params.search = search;
      const [offRes, cliRes, prodRes] = await Promise.allSettled([
        commercialOfferService.getAll(params),
        clientService.getAll(),
        productService.getAll(),
      ]);
      if (offRes.status === 'fulfilled') setOffers(offRes.value.data);
      else toast.error('Errore caricamento offerte');
      if (cliRes.status === 'fulfilled') setClients(cliRes.value.data.filter(c => c.is_active !== false));
      if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  }, [filterClient, filterStatus, filterYear, search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.subject || !form.request_date) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    const payload = { ...form, sent_date: form.sent_date || null, product_id: form.product_id || null };
    try {
      if (editOffer) {
        await commercialOfferService.update(editOffer.id, payload);
        toast.success('Offerta aggiornata');
      } else {
        await commercialOfferService.create(payload);
        toast.success('Offerta creata');
      }
      setShowForm(false);
      setEditOffer(null);
      setForm(emptyForm());
      load();
    } catch { toast.error('Errore salvataggio'); }
  };

  const openEdit = (offer) => {
    setEditOffer(offer);
    setForm({
      client_id: offer.client_id || '',
      request_date: offer.request_date || '',
      sent_date: offer.sent_date || '',
      subject: offer.subject || '',
      product_id: offer.product_id || '',
      status: offer.status || 'da_valutare',
      notes: offer.notes || '',
    });
    setShowForm(true);
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

  return (
    <div className="space-y-4" data-testid="offers-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="offers-title">Registro Offerte</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestione richieste e offerte commerciali</p>
        </div>
        <Button size="sm" onClick={() => { setEditOffer(null); setForm(emptyForm()); setShowForm(true); }} data-testid="new-offer-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova Offerta
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap gap-3 items-end" data-testid="offers-filters">
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Anno</label>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value ? parseInt(e.target.value) : '')}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[90px]" data-testid="filter-year">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cliente</label>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[160px]" data-testid="filter-client">
            <option value="">Tutti</option>
            {clients.map(c => <option key={c.id} value={c.id}>{`${c.code} – ${c.name}`}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Stato</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[130px]" data-testid="filter-status">
            <option value="">Tutti</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cerca</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="N. offerta o oggetto..."
              className="w-full text-sm border border-slate-200 rounded-md pl-8 pr-3 py-1.5 focus:ring-2 focus:ring-slate-900 focus:outline-none" data-testid="search-input" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="offers-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : offers.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Nessuna offerta trovata</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-2.5">N. Offerta</th>
                  <th className="px-4 py-2.5">Cliente</th>
                  <th className="px-4 py-2.5">Data Arrivo</th>
                  <th className="px-4 py-2.5">Data Invio</th>
                  <th className="px-4 py-2.5 text-center">Lead Time</th>
                  <th className="px-4 py-2.5">Stato</th>
                  <th className="px-4 py-2.5">Oggetto</th>
                  <th className="px-4 py-2.5 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {offers.map(o => (
                  <tr key={o.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => nav(`/commercial-offers/${o.id}`)} data-testid={`offer-row-${o.id}`}>
                    <td className="px-4 py-2.5 font-mono font-semibold text-slate-900">{o.offer_number}</td>
                    <td className="px-4 py-2.5 text-slate-700">{o.client_name || o.client_code || '-'}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(o.request_date)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(o.sent_date)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {o.lead_time_days !== null && o.lead_time_days !== undefined
                        ? <span className={`font-mono text-xs font-medium px-1.5 py-0.5 rounded ${o.lead_time_days <= 3 ? 'bg-emerald-100 text-emerald-800' : o.lead_time_days <= 7 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{o.lead_time_days}g</span>
                        : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2.5">{statusBadge(o.status)}</td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-[200px] truncate">{o.subject}</td>
                    <td className="px-4 py-2.5 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="offer-form-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4" /> {editOffer ? 'Modifica Offerta' : 'Nuova Offerta'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cliente *</label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" required data-testid="form-client">
                  <option value="">Seleziona cliente...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{`${c.code} – ${c.name}`}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Oggetto / Descrizione *</label>
                <input type="text" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" required placeholder="es. Offerta per lavorazione alberi motore" data-testid="form-subject" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Data Arrivo Richiesta *</label>
                  <input type="date" value={form.request_date} onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" required data-testid="form-request-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Data Invio Offerta</label>
                  <input type="date" value={form.sent_date} onChange={e => setForm(f => ({ ...f, sent_date: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2" data-testid="form-sent-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Stato</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid="form-status">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Prodotto</label>
                  <select value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid="form-product">
                    <option value="">Nessuno</option>
                    {products.map(p => <option key={p.id} value={p.id}>{`${p.code} – ${p.name}`}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Note</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 resize-y" rows={3}
                  placeholder="Dettagli richiesta, requisiti tecnici..." data-testid="form-notes" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Annulla</Button>
                <Button type="submit" size="sm" data-testid="form-submit">{editOffer ? 'Aggiorna' : 'Crea Offerta'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
